import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw, Loader2 } from 'lucide-react';
import { API_URL } from '../../utils/config';

/**
 * Sotto-pagina di Gestione Social: matrice mensile delle pubblicazioni.
 * - Righe: pagine social (Facebook/Instagram di HW)
 * - Colonne: giorni del mese
 * - Cella: "X" se quel giorno e' stato pubblicato almeno un post da quella pagina
 * - Selettore mese (frecce + selettore mese/anno)
 * - Solo lettura (no click), nessun numero/multiplo
 */
const PublishedCalendarTab = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  useEffect(() => {
    fetchData();
  }, [year, month]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API_URL}/api/social/calendar/published?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const d = await r.json();
        setData(d);
      } else {
        const e = await r.json().catch(() => ({}));
        setError(e.detail || 'Errore caricamento calendario');
      }
    } catch {
      setError('Errore di rete');
    }
    setLoading(false);
  };

  const goPrev = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const goNext = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); };

  const days = useMemo(() => {
    if (!data) return [];
    return Array.from({ length: data.days_in_month }, (_, i) => i + 1);
  }, [data]);

  const isToday = (day) => {
    return year === today.getFullYear() && month === today.getMonth() + 1 && day === today.getDate();
  };

  // Day-of-week index per columna (per evidenziare weekend)
  const getDow = (day) => {
    const d = new Date(year, month - 1, day);
    return d.getDay(); // 0=Sun, 6=Sat
  };

  // Conta totale pubblicazioni nel mese (per status footer)
  const stats = useMemo(() => {
    if (!data) return { totalCells: 0, publishedCells: 0, activePages: 0 };
    let totalCells = 0;
    let publishedCells = 0;
    let activePages = 0;
    Object.values(data.matrix).forEach((row) => {
      totalCells += row.length;
      const cnt = row.filter(Boolean).length;
      publishedCells += cnt;
      if (cnt > 0) activePages += 1;
    });
    return { totalCells, publishedCells, activePages };
  }, [data]);

  return (
    <div className="space-y-4" data-testid="published-calendar-tab">
      {/* Header con selettore mese */}
      <Card>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-blue-600" />
            <h2 className="text-base font-semibold text-slate-800">
              Calendario Pubblicazioni — {monthNames[month - 1]} {year}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} data-testid="btn-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToday} data-testid="btn-today">Oggi</Button>
            <Button variant="outline" size="sm" onClick={goNext} data-testid="btn-next-month">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-slate-600" data-testid="btn-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabella matrice */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento...
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-red-600 bg-red-50">{error}</div>
          ) : !data || (data.pages || []).length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">Nessuna pagina social configurata.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="border-collapse w-full text-xs">
                <thead className="sticky top-0 bg-slate-100 z-10">
                  <tr>
                    <th className="sticky left-0 bg-slate-100 text-left px-3 py-2 border-b border-r border-slate-200 w-56 z-20" data-testid="col-page-name">
                      Pagina Social
                    </th>
                    {days.map((day) => {
                      const dow = getDow(day);
                      const weekend = dow === 0 || dow === 6;
                      const todayCell = isToday(day);
                      return (
                        <th
                          key={day}
                          className={`text-center px-1 py-2 border-b border-r border-slate-200 w-8 font-medium ${weekend ? 'bg-slate-50' : ''} ${todayCell ? 'bg-blue-100 text-blue-900' : ''}`}
                          data-testid={`col-day-${day}`}
                        >
                          {day}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.pages.map((page) => {
                    const row = data.matrix[page.id] || [];
                    return (
                      <tr key={page.id} className="hover:bg-slate-50" data-testid={`row-page-${page.id}`}>
                        <td className="sticky left-0 bg-white px-3 py-1.5 border-b border-r border-slate-200 font-medium text-slate-800 truncate" title={page.name}>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase text-slate-400 w-6">{(page.platform || 'fb').slice(0,2)}</span>
                            <span className="truncate">{page.name}</span>
                          </div>
                        </td>
                        {days.map((day) => {
                          const dow = getDow(day);
                          const weekend = dow === 0 || dow === 6;
                          const todayCell = isToday(day);
                          const published = row[day - 1] === true;
                          return (
                            <td
                              key={day}
                              className={`text-center border-b border-r border-slate-200 align-middle ${weekend ? 'bg-slate-50/60' : ''} ${todayCell ? 'bg-blue-50' : ''}`}
                              data-testid={`cell-${page.id}-${day}`}
                            >
                              {published ? (
                                <span className="inline-flex items-center justify-center w-5 h-5 text-emerald-700 font-bold text-sm" aria-label="Pubblicato">
                                  X
                                </span>
                              ) : (
                                <span className="text-slate-300">·</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistiche footer */}
      {data && !loading && (
        <Card>
          <CardContent className="p-3 flex items-center gap-6 text-xs text-slate-600 flex-wrap" data-testid="calendar-footer-stats">
            <div><b className="text-slate-800">{data.pages.length}</b> pagine totali</div>
            <div><b className="text-emerald-700">{stats.activePages}</b> pagine con almeno 1 pubblicazione</div>
            <div><b className="text-emerald-700">{stats.publishedCells}</b> giorni-pagina con pubblicazione</div>
            <div className="text-slate-400">
              X = pubblicato · · = nessuna pubblicazione · sfondo grigio = weekend · sfondo blu = oggi
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PublishedCalendarTab;
